import create from 'zustand';
import {useShallow} from 'zustand/react/shallow';
import {getLocalStorage, setLocalStorage} from '@/Utils/CommonUtils';
import {
  computeOnboardingSteps,
  ONBOARDING_STEPS,
  StepKey,
} from '@/Utils/onboardingSteps';

const FLAGS_KEY = 'onboarding_progress_flags';
// Persisted, so the welcome nudge fires exactly ONCE per user, ever — matching
// cc-learning-app. Ongoing prompting is handled by the always-visible surfaces
// instead (the header ring + completion %, the avatar dropdown row and the
// Settings card), which nag without interrupting.
const WELCOME_KEY = 'onboarding_welcome_seen';

type Flags = Partial<Record<StepKey, boolean>>;

interface State {
  flags: Flags;
  summarySnapshot: any;
  welcomeSeen: boolean;
  hydrated: boolean;
  // user_key the persisted flags/welcome flag belong to. Both storage keys are
  // browser-global, and flags are latch-only (never un-set), so without this a
  // second account signing in on the same browser inherited the first
  // account's 100%: the header completion bar stayed hidden and the welcome
  // nudge stayed suppressed. On web this is the norm, not an edge case.
  owner: string | null;
  bootstrap: () => Promise<void>;
  hydrateFromSummary: (summary: any) => void;
  markStepDone: (key: StepKey) => void;
  setWelcomeSeen: () => void;
}

// setLocalStorage no-ops on a falsy value, so we always persist a non-empty
// object (the __v guard also future-proofs the persisted shape).
const persistFlags = (flags: Flags, owner: string | null) =>
  setLocalStorage(FLAGS_KEY, {...flags, __v: 1, __owner: owner || ''});

export const useOnboardingProgressStore = create<State>((set, getState) => ({
  flags: {},
  summarySnapshot: {},
  welcomeSeen: false,
  hydrated: false,
  owner: null,

  bootstrap: async () => {
    const [savedFlags, savedWelcome] = await Promise.all([
      getLocalStorage(FLAGS_KEY),
      getLocalStorage(WELCOME_KEY),
    ]);
    const clean: Flags = {};
    if (savedFlags) {
      for (const step of ONBOARDING_STEPS) {
        if ((savedFlags as any)[step.key]) clean[step.key] = true;
      }
    }
    // The signed-in user isn't known yet at cold start; hydrateFromSummary
    // resets these if the owner turns out to be someone else.
    set({
      flags: clean,
      // {seen:false} is a truthy OBJECT, so a bare !!savedWelcome would read a
      // reset flag as "already seen" and swallow the nudge. Trust the `seen`
      // field when present; fall back to truthiness only for the legacy shape.
      welcomeSeen:
        savedWelcome && typeof savedWelcome === 'object' && 'seen' in savedWelcome
          ? !!(savedWelcome as any).seen
          : !!savedWelcome,
      hydrated: true,
      owner:
        ((savedFlags as any)?.__owner || (savedWelcome as any)?.owner || null) ||
        null,
    });
  },

  hydrateFromSummary: summary => {
    const nextOwner = summary?.user?.user_key
      ? String(summary.user.user_key)
      : null;
    const prevOwner = getState().owner;
    // Different account on this browser -> start their progress from scratch
    // rather than inheriting the previous user's latched flags.
    const switchedUser = !!nextOwner && !!prevOwner && nextOwner !== prevOwner;

    const next: Flags = switchedUser ? {} : {...getState().flags};
    for (const step of ONBOARDING_STEPS) {
      if (step.summaryDone && step.summaryDone(summary)) next[step.key] = true;
    }
    const owner = nextOwner || prevOwner;
    set({
      summarySnapshot: summary || {},
      flags: next,
      owner,
      ...(switchedUser ? {welcomeSeen: false} : {}),
    });
    persistFlags(next, owner);
    if (switchedUser) {
      setLocalStorage(WELCOME_KEY, {seen: false, owner});
    }
  },

  markStepDone: key => {
    if (getState().flags[key]) return;
    const next = {...getState().flags, [key]: true};
    set({flags: next});
    persistFlags(next, getState().owner);
  },

  setWelcomeSeen: () => {
    set({welcomeSeen: true});
    setLocalStorage(WELCOME_KEY, {seen: true, owner: getState().owner});
  },
}));

// Derived selectors — recompute from the latched flags + latest summary.
export const useOnboardingProgress = () => {
  const [flags, summary] = useOnboardingProgressStore(
    useShallow(s => [s.flags, s.summarySnapshot]),
  );
  return computeOnboardingSteps(summary, flags);
};

export const useOnboardingPercent = () => useOnboardingProgress().percent;
export const useOnboardingSteps = () => useOnboardingProgress().steps;
export const useOnboardingComplete = () =>
  useOnboardingProgress().percent >= 100;

export default useOnboardingProgressStore;
